<script setup>
import { computed } from 'vue';

const props = defineProps({
  // Content and appearance
  message: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: 'spinner', // 'spinner' or 'pulse'
  },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg'].includes(value),
  },

  // Color schemes
  variant: {
    type: String,
    default: 'primary',
    validator: value =>
      ['primary', 'secondary', 'success', 'warning', 'danger'].includes(value),
  },

  // Positioning
  position: {
    type: String,
    default: 'inline',
    validator: value => ['inline', 'fixed', 'absolute'].includes(value),
  },
  placement: {
    type: String,
    default: 'bottom-right',
    validator: value =>
      [
        'top-left',
        'top-center',
        'top-right',
        'center-left',
        'center',
        'center-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
      ].includes(value),
  },

  // Styling options
  rounded: {
    type: Boolean,
    default: true,
  },
  shadow: {
    type: Boolean,
    default: true,
  },
  backdrop: {
    type: Boolean,
    default: false,
  },

  // Z-index
  zIndex: {
    type: Number,
    default: 50,
  },
});

const sizeClasses = computed(() => {
  const sizes = {
    sm: {
      container: 'px-3 py-2 text-xs font-medium',
      icon: 'w-3 h-3',
      gap: 'gap-2',
    },
    md: {
      container: 'px-4 py-3 text-sm font-medium',
      icon: 'w-4 h-4',
      gap: 'gap-3',
    },
    lg: {
      container: 'px-5 py-4 text-base font-medium',
      icon: 'w-5 h-5',
      gap: 'gap-4',
    },
  };

  return sizes[props.size];
});

const variantClasses = computed(() => {
  const variants = {
    primary: {
      background: 'bg-white dark:bg-n-slate-1',
      border: 'border-n-slate-3 dark:border-n-slate-7',
      text: 'text-n-slate-11',
      icon: 'text-n-slate-11',
    },
    secondary: {
      background: 'bg-white dark:bg-n-slate-1',
      border: 'border-n-slate-3 dark:border-n-slate-7',
      text: 'text-n-slate-11',
      icon: 'text-n-slate-11',
    },
    success: {
      background: 'bg-white dark:bg-n-slate-1',
      border: 'border-n-teal-3 dark:border-n-teal-7',
      text: 'text-n-teal-11',
      icon: 'text-n-teal-11',
    },
    warning: {
      background: 'bg-white dark:bg-n-slate-1',
      border: 'border-n-amber-3 dark:border-n-amber-7',
      text: 'text-n-amber-11',
      icon: 'text-n-amber-11',
    },
    danger: {
      background: 'bg-white dark:bg-n-slate-1',
      border: 'border-n-ruby-3 dark:border-n-ruby-7',
      text: 'text-n-ruby-11',
      icon: 'text-n-ruby-11',
    },
  };

  return variants[props.variant];
});

const positionClasses = computed(() => {
  if (props.position === 'inline') {
    return 'relative inline-flex';
  }

  const baseClasses = props.position === 'fixed' ? 'fixed' : 'absolute';

  const placements = {
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
    'top-right': 'top-4 right-4',
    'center-left': 'top-1/2 left-4 transform -translate-y-1/2',
    center: 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2',
    'center-right': 'top-1/2 right-4 transform -translate-y-1/2',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
  };

  return `${baseClasses} ${placements[props.placement]}`;
});

const containerClasses = computed(() => {
  const classes = [
    // Base layout
    'flex items-center',
    sizeClasses.value.container,
    sizeClasses.value.gap,

    // Colors
    variantClasses.value.background,
    variantClasses.value.text,

    // Border
    'border',
    variantClasses.value.border,

    // Position
    positionClasses.value,

    // Z-index
    `z-${props.zIndex}`,
  ];

  // Conditional styling
  if (props.rounded) {
    classes.push('rounded-lg');
  }

  if (props.shadow) {
    classes.push('shadow-lg');
  }

  return classes.join(' ');
});

const iconClasses = computed(() => {
  const classes = [sizeClasses.value.icon, variantClasses.value.icon];

  if (props.icon === 'spinner') {
    classes.push('animate-spin');
  } else if (props.icon === 'pulse') {
    classes.push('animate-pulse');
  }

  return classes.join(' ');
});
</script>

<template>
  <!-- Backdrop (optional) -->
  <div
    v-if="backdrop && position !== 'inline'"
    class="fixed inset-0 bg-black bg-opacity-25 z-40"
  />

  <!-- Loading indicator -->
  <div :class="containerClasses">
    <!-- Icon -->
    <div v-if="icon === 'spinner'">
      <svg
        :class="iconClasses"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>

    <div v-else-if="icon === 'pulse'">
      <div :class="iconClasses" class="bg-current rounded-full" />
    </div>

    <!-- Custom icon slot -->
    <slot v-else name="icon">
      <div :class="iconClasses">
        <i :class="icon" />
      </div>
    </slot>

    <!-- Message -->
    <span v-if="message || $slots.default">
      <slot>{{ message }}</slot>
    </span>
  </div>
</template>

<style scoped>
/* Custom animations for pulse icon */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
